/**
 * Add people to a group (plan docs/plans/lms-plan.md 8.2 "members (a picker
 * over the network's users ... with search and multi-select)", 18.20; WP-43;
 * R6, R19).
 *
 * The UserPicker over the directory (people the admin reaches, G14), with the
 * current members shown but not choosable. Saving posts
 * POST /admin/groups/:id/members {user_ids} (at most 500 per request, 18.20)
 * and hands the answer ({ added, already_member, reconcile }) back.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import { primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { addGroupMembers, MAX_MEMBER_IDS } from '../../../api/groups';
import type { AddMembersResult } from '../../../api/groups';
import UserPicker from '../../../components/pickers/UserPicker';
import { ErrorNote, peopleCount } from '../../../components/pickers/shared';

export interface AddMembersModalProps {
  isOpen: boolean;
  groupId: string;
  groupName: string;
  memberIds: string[];
  onClose: () => void;
  onAdded: (result: AddMembersResult) => void;
}

export function AddMembersModal({ isOpen, groupId, groupName, memberIds, onClose, onAdded }: AddMembersModalProps) {
  const [chosen, setChosen] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setChosen([]);
    setSaving(false);
    setError(null);
  }, [isOpen]);

  const tooMany = chosen.length > MAX_MEMBER_IDS;

  const save = async () => {
    if (chosen.length === 0 || tooMany) return;
    setSaving(true);
    setError(null);
    try {
      const result = await addGroupMembers(groupId, chosen);
      onAdded(result);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add the people.'));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Add people to ${groupName}`} maxWidth="max-w-2xl">
      <div className="space-y-4" data-testid="add-members">
        <UserPicker value={chosen} onChange={(ids) => setChosen(ids)} excludeIds={memberIds} excludedNote="Already a member" />
        {tooMany && <ErrorNote>{`Add at most ${MAX_MEMBER_IDS} people at a time.`}</ErrorNote>}
        {error && <ErrorNote testId="add-members-error">{error}</ErrorNote>}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className={primaryBtnCls}
            onClick={save}
            disabled={saving || chosen.length === 0 || tooMany}
            data-testid="add-members-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {chosen.length === 0 ? 'Add people' : `Add ${peopleCount(chosen.length)}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default AddMembersModal;
